import { Card, Divider, Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import { FormikValues } from 'formik';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppealStatus } from 'src/enums/application-status.enum';
import { setAppealConfirm, setAppealDecline } from 'src/thunks/appeal-thunks';
import { getFullName } from '../../../common/utils/format-utils';
import { dateFormat, useDialog } from '../../../hooks';
import { AppealsConfirmDialog } from '../appeals-confirm-dialog';
import { AppealsDeclineDialog } from '../appeals-decline-dialog';
import { AppealsDialog } from '../appeals-dialog';
import useStyles from './styles';

interface AppealsCardProps {
  className?: string;
  item: Record<string, any>;
}

export const AppealsCard: ComponentType<AppealsCardProps> = ({ className, item }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [openDetailsDialog] = useDialog(AppealsDialog);
  const [openConfirmDialog, closeConfirmDialog] = useDialog(AppealsConfirmDialog);
  const [openDeclineDialog, closeDeclineDialog] = useDialog(AppealsDeclineDialog);

  const { id, uid, user, updatedAt, createdAt } = item;

  const handleDetails = () => {
    openDetailsDialog({
      item,
      size: 'large',
      resolve: resolveSubmit,
      reject: rejectSubmit,
    });
  };

  const resolveSubmit = () => {
    openConfirmDialog({
      onSubmit: confirmAppeal,
      title: t('application.confirm.title', 'Одобрить заявку'),
      text: t(
        'application.confirm.text',
        `Вы точно хотите принять заявку на апелляцию от пользователя ${getFullName(user)}?`,
        {
          userName: getFullName(user),
        }
      ),
      id,
      size: 'small',
    });
  };

  const rejectSubmit = () => {
    openDeclineDialog({
      onSubmit: declineAppeal,
      title: t('appeal.decline.title', 'Отклонить апелляцию'),
      text: t(
        'appeal.decline.text',
        `Вы точно хотите отклонить заявку на апелляцию от пользователя ${getFullName(user)}?`,
        { userName: getFullName(user) }
      ),
      id,
      size: 'small',
    });
  };

  const confirmAppeal = (values: FormikValues) => {
    dispatch(setAppealConfirm(values));
    closeConfirmDialog();
  };
  const declineAppeal = (values: FormikValues) => {
    dispatch(setAppealDecline(values));
    closeDeclineDialog();
  };

  return (
    <Card className={clsx(classes.root, className)}>
      <Typography variant="subtitle1" component="h5" mb={2}>
        {t('appeals.request.title', `Заявка на апелляцию №`)} {uid}
      </Typography>
      <Typography variant="body3" component="p" color="grey.800" mb={1}>
        {t('name.full', 'ФИО')}: {getFullName(user)}
      </Typography>
      <Typography variant="body3" component="p" color="grey.800" mb={1}>
        {t('date.attestation', 'Дата аттестации')}: {dateFormat(updatedAt)}
      </Typography>
      <Typography variant="body3" component="p" color="grey.800" mb={2}>
        {t('appeal.status', 'Статус апелляции')}:{' '}
        <span
          className={clsx(
            { [classes.statusConfirmed]: item.appealStatus === AppealStatus.CONFIRMED },
            { [classes.statusDeclined]: item.appealStatus === AppealStatus.DECLINED }
          )}
        >
          {t(`appealStatus.${item.appealStatus}`)}
        </span>
      </Typography>
      <Divider sx={{ mt: 1.5, mb: 1.5 }} />
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        className={classes.footer}
        spacing={2}
      >
        <Typography variant="caption" color="grey.400" className={classes.date}>
          {dateFormat(createdAt)}
        </Typography>

        <Typography
          onClick={handleDetails}
          variant="body3"
          className={classes.link}
          color="primary"
        >
          {t('action.more', 'Подробнее')}...
        </Typography>
      </Stack>
    </Card>
  );
};
